<template>
  <b-modal
    id="viewQrCode"
    hide-footer
    no-close-on-backdrop
    modal-class="qr-modal-modern"
    @shown="generateQr"
    @hidden="resetTimer"
    size="lg"
  >
    <template v-slot:modal-title>
      <div class="modal-title-modern">
        <i class="fab fa-whatsapp mr-2"></i>
        Conectar WhatsApp
      </div>
    </template>

    <div class="modal-content-modern">
      <!-- Tutorial Section -->
      <div class="tutorial-section">
        <div class="tutorial-header">
          <i class="fas fa-mobile-alt tutorial-icon"></i>
          <h4 class="tutorial-title">Como Escanear</h4>
        </div>

        <div class="tutorial-steps">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-text">Abra o WhatsApp no seu celular</div>
          </div>

          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-text">
              Toque em <strong>Mais opções</strong> (Android) ou
              <strong>Configurações</strong> (iPhone)
            </div>
          </div>

          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-text">
              Toque em <strong>Dispositivos conectados</strong> e depois em
              <strong>Conectar dispositivo</strong>
            </div>
          </div>

          <div class="step-item">
            <div class="step-number">4</div>
            <div class="step-text">Escaneie o QR Code ao lado</div>
          </div>
        </div>

        <div class="tutorial-alert">
          <i class="fas fa-info-circle alert-icon"></i>
          <p class="alert-text">
            Se aparecer alguma mensagem de erro no WhatsApp, clique em
            <strong>Gerar Novamente</strong>
          </p>
        </div>
      </div>

      <!-- QR Code Section -->
      <div class="qr-section">
        <div class="qr-container">
          <!-- Loading State -->
          <div
            v-show="(!qrCode && !connected && timeLeft > 0) || loading"
            class="qr-loading"
          >
            <div class="loading-spinner"></div>
            <p class="loading-text">Gerando QR Code...</p>
          </div>

          <!-- QR Code Display -->
          <div v-show="qrCode && !connected && timeLeft > 0" class="qr-display">
            <p class="qr-instruction">Aponte a câmera do celular:</p>
            <div class="qr-frame">
              <img :src="qrCode" alt="QR Code" class="qr-image" />
              <div class="qr-corners">
                <div class="corner top-left"></div>
                <div class="corner top-right"></div>
                <div class="corner bottom-left"></div>
                <div class="corner bottom-right"></div>
              </div>
            </div>
            <div class="qr-status waiting">
              <div class="status-dot"></div>
              <span>Aguardando conexão...</span>
            </div>
          </div>

          <!-- Expired State -->
          <div
            v-show="
              timeLeft == 0 && !connected && errors.length == 0 && !loading
            "
            class="qr-expired"
          >
            <i class="fas fa-clock expired-icon"></i>
            <p class="expired-title">QR Code Expirado</p>
            <p class="expired-text">
              Clique no botão abaixo para gerar um novo código
            </p>
            <v-btn class="btn-regenerate" @click="generateQr">
              <i class="fas fa-sync-alt mr-2"></i>
              Gerar Novamente
            </v-btn>
          </div>

          <!-- Connected State -->
          <div v-show="connected" class="qr-success">
            <div class="success-animation">
              <i class="fas fa-check-circle success-icon"></i>
            </div>
            <p class="success-title">WhatsApp Conectado!</p>
            <p class="success-text">Conexão estabelecida com sucesso</p>
          </div>

          <!-- Error State -->
          <div v-show="errors.length > 0 && !loading" class="qr-error">
            <i class="fas fa-exclamation-triangle error-icon"></i>

            <p class="error-title">Erro ao Conectar</p>

            <ErrorsMessages :errors="errors" />

            <v-btn class="btn-retry" @click="generateQr">
              <i class="fas fa-redo mr-2"></i>
              Tentar Novamente
            </v-btn>
          </div>
        </div>

        <!-- Timer Footer -->
        <div class="timer-footer">
          <div
            v-show="timeLeft > 0 && !connected && errors.length == 0"
            class="timer-active"
          >
            <i class="fas fa-clock timer-icon"></i>
            <span class="timer-label">Tempo Restante:</span>
            <span class="timer-value">{{ timeLeft }}s</span>
          </div>

          <div
            v-show="timeLeft == 0 && !connected && errors.length == 0"
            class="timer-expired"
          >
            <i class="fas fa-times-circle timer-icon"></i>
            <span>QR Code Expirado</span>
          </div>

          <div v-show="connected" class="timer-connected">
            <i class="fas fa-check-circle timer-icon"></i>
            <span>WhatsApp Conectado</span>
          </div>

          <div v-show="errors.length > 0" class="timer-error">
            <i class="fas fa-exclamation-circle timer-icon"></i>
            <span>Erro na Conexão</span>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ErrorsMessages from "../Components/ErrorsMessages";

import api from "../../services/api";

export default {
  name: "ModalViewQrCode",

  components: {
    ErrorsMessages,
  },

  props: {
    type: String,
    rest_home_id: Number,
    phone_number: String,
  },

  data() {
    return {
      loading: false,

      qrCode: null,

      connected: false,

      visible: false,

      timeLeft: 30,

      timerId: null,

      message: "",

      errors: [],
    };
  },

  methods: {
    async listInstance() {
      try {
        let { type, rest_home_id } = this;

        let params = {
          instanceName: `SISTEMA_CLIN_${type}_${rest_home_id}`,
          type,
        };

        const response = await api.get("/whatsapp/list-instance", { params });

        const { connected, errors } = response.data;

        if (!connected) {
          this.errors = errors;
        }

        this.connected = connected;
      } catch (error) {
        console.log(error);
      }
    },

    async generateQr() {
      try {
        this.loading = true;

        this.qrCode = null;

        this.errors = [];

        let { type, rest_home_id, phone_number } = this;
        let params = {
          instanceName: `SISTEMA_CLIN_${type}_${rest_home_id}`,
          phone_number,
        };

        const response = await api.post("/whatsapp/connect-instance", params);
        const { error, qrCode, errors } = response.data;

        if (!error) {
          this.errors = [];
          this.qrCode = qrCode;
        } else {
          this.errors = errors;
        }

        this.handleTimer();
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },

    startTimer() {
      this.timeLeft = 30;
      this.timerId = setInterval(() => {
        if (this.timeLeft > 0) {
          this.timeLeft--;

          if (this.errors.length == 1) {
            this.timeLeft = 0;
          }

          if (!this.connected) {
            if (
              this.timeLeft == 5 ||
              this.timeLeft == 12 ||
              this.timeLeft == 20
            ) {
              this.listInstance();
            }
          }
        } else {
          clearInterval(this.timerId);
          this.$emit("timeout");
        }
      }, 1000);
    },

    resetTimer() {
      this.connected = false;
      clearInterval(this.timerId);
      this.timeLeft = 30;
      this.$emit("connect");
    },

    handleTimer() {
      this.resetTimer();
      this.startTimer();
    },
  },
};
</script>

<style scoped>
.modal-title-modern {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.modal-title-modern i {
  color: #10b981;
}

.modal-content-modern {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  padding: 8px 0;
}

/* Tutorial Section */
.tutorial-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.tutorial-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.tutorial-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  padding: 10px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
}

.tutorial-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.tutorial-steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.step-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(15, 101, 176, 0.3);
}

.step-text {
  flex: 1;
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  padding-top: 2px;
}

.step-text strong {
  color: #1e293b;
  font-weight: 600;
}

.tutorial-alert {
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  border: 1px solid #818cf8;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.alert-icon {
  color: #4338ca;
  font-size: 18px;
  margin-top: 2px;
  flex-shrink: 0;
}

.alert-text {
  flex: 1;
  font-size: 13px;
  color: #3730a3;
  line-height: 1.5;
  margin: 0;
}

.alert-text strong {
  font-weight: 600;
}

/* QR Section */
.qr-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.qr-container {
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  border: 2px dashed #e5e7eb;
}

/* Loading State */
.qr-loading {
  text-align: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* QR Code Display */
.qr-display {
  text-align: center;
  width: 100%;
}

.qr-instruction {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin: 0 0 20px 0;
}

.qr-frame {
  position: relative;
  width: 220px;
  height: 220px;
  margin: 0 auto 20px;
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.qr-image {
  width: 100%;
  height: 100%;
  border-radius: 8px;
}

.qr-corners {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.corner {
  position: absolute;
  width: 24px;
  height: 24px;
  border: 3px solid #10b981;
}

.corner.top-left {
  top: 8px;
  left: 8px;
  border-right: none;
  border-bottom: none;
  border-radius: 8px 0 0 0;
}

.corner.top-right {
  top: 8px;
  right: 8px;
  border-left: none;
  border-bottom: none;
  border-radius: 0 8px 0 0;
}

.corner.bottom-left {
  bottom: 8px;
  left: 8px;
  border-right: none;
  border-top: none;
  border-radius: 0 0 0 8px;
}

.corner.bottom-right {
  bottom: 8px;
  right: 8px;
  border-left: none;
  border-top: none;
  border-radius: 0 0 8px 0;
}

.qr-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.qr-status.waiting {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #92400e;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f59e0b;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
}

/* Expired State */
.qr-expired {
  text-align: center;
}

.expired-icon {
  font-size: 64px;
  color: #f59e0b;
  margin-bottom: 16px;
}

.expired-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.expired-text {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px 0;
}

.btn-regenerate {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 32px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  font-size: 13px !important;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-regenerate:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4) !important;
}

/* Success State */
.qr-success {
  text-align: center;
}

.success-animation {
  margin-bottom: 16px;
}

.success-icon {
  font-size: 64px;
  color: #10b981;
  animation: successPop 0.5s ease-out;
}

@keyframes successPop {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.success-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.success-text {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Error State */
.qr-error {
  text-align: center;
}

.error-icon {
  font-size: 64px;
  color: #ef4444;
  margin-bottom: 16px;
}

.error-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px 0;
}

.btn-retry {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 32px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  font-size: 13px !important;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3) !important;
  transition: all 0.3s ease !important;
  margin-top: 16px !important;
}

.btn-retry:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4) !important;
}

/* Timer Footer */
.timer-footer {
  background: white;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.timer-active,
.timer-expired,
.timer-connected,
.timer-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
}

.timer-active {
  color: #374151;
}

.timer-expired {
  color: #f59e0b;
}

.timer-connected {
  color: #10b981;
}

.timer-error {
  color: #ef4444;
}

.timer-icon {
  font-size: 16px;
}

.timer-label {
  font-weight: 500;
}

.timer-value {
  font-size: 18px;
  font-weight: 700;
  color: #0f65b0;
  min-width: 40px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-content-modern {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .qr-container {
    padding: 24px;
    min-height: 320px;
  }

  .qr-frame {
    width: 180px;
    height: 180px;
  }

  .tutorial-section {
    order: 2;
  }

  .qr-section {
    order: 1;
  }
}

@media (max-width: 480px) {
  .qr-container {
    padding: 16px;
  }

  .qr-frame {
    width: 160px;
    height: 160px;
    padding: 12px;
  }

  .step-item {
    gap: 10px;
  }

  .step-number {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }

  .expired-icon,
  .success-icon,
  .error-icon {
    font-size: 48px;
  }
}
</style>